import { createNonNullableContext } from '@joyful/utils'
import type { DependencyList, ReactElement } from 'react'
import { useEffect, useEffectEvent } from 'react'

interface AppNavigationBaseItem {
  description?: string
  endAdornment?: ReactElement
  key: string
  keywords?: Array<string>
  startAdornment?: string | ReactElement
  title: string
}

export interface AppNavigationLinkItem extends AppNavigationBaseItem {
  href?: string
  items?: never
  onSelect?: never
  path: string
  search?: string
  type?: 'link'
}

/** Same-app path plus search, or the cross-app href when present. */
export function getAppNavigationLinkTo(
  item: Pick<AppNavigationLinkItem, 'href' | 'path' | 'search'>,
) {
  if (item.href) {
    return item.href
  }

  if (item.search) {
    return `${item.path}${item.search}`
  }

  return item.path
}

export interface AppNavigationActionItem extends AppNavigationBaseItem {
  href?: never
  items?: never
  onSelect: () => void
  path?: never
  type?: 'action'
}

export interface AppNavigationGroupItem extends AppNavigationBaseItem {
  href?: never
  items: ReadonlyArray<AppNavigationLinkItem>
  onSelect?: never
  path?: never
  type: 'group'
}

export type AppNavigationItem =
  | AppNavigationActionItem
  | AppNavigationGroupItem
  | AppNavigationLinkItem

export type AppRouteNavigationSection = 'account' | 'general' | 'primary'

export interface GlobalCommandContextValue {
  open: () => void
  register: (item: AppNavigationItem) => () => void
  unregister: (key: string) => void
}

export const GlobalCommandContext =
  createNonNullableContext<GlobalCommandContextValue>('GlobalCommandContext')

export const useGlobalCommand = GlobalCommandContext.useRequiredContext

export function useRegisterGlobalCommands(
  createItems: () => Array<AppNavigationItem>,
  deps: DependencyList = [],
) {
  const globalCommand = useGlobalCommand()

  const registerCommands = useEffectEvent(() => {
    const items = createItems()
    const unregisterFns: Array<() => void> = []

    for (const item of items) {
      const unregisterItem = globalCommand.register(item)
      unregisterFns.push(unregisterItem)
    }

    return unregisterFns
  })

  useEffect(() => {
    const unregisterFns = registerCommands()

    return () => {
      for (const unregister of unregisterFns) {
        unregister()
      }
    }
    // Callers pass the dependency list for their command factory.
    // react-doctor-disable-next-line react-doctor/exhaustive-deps
  }, deps)
}
